import { useState } from "react";
import { toast } from "sonner";
import { Copy, Check, Globe, ChevronDown, ChevronUp, ExternalLink } from "lucide-react";

import { BACKEND_URL_BASE } from "../services/api";

const BACKEND_URL = BACKEND_URL_BASE;
const BASE_URL = `${BACKEND_URL}/api/public/v1`;

const ENDPOINTS = [
  {
    method: "GET",
    path: "/questions",
    title: "Tüm Soruları Listele",
    description: "Aktif soruların sayfalı listesini döner. Zengin filtreleme desteği.",
    params: [
      { name: "page", type: "integer", default: "1", desc: "Sayfa numarası" },
      { name: "limit", type: "integer", default: "20", desc: "Sayfa başına kayıt (max 100)" },
      { name: "archive_name", type: "string", desc: "Arşiv adı filtresi" },
      { name: "lesson_name", type: "string", desc: "Ders adı filtresi" },
      { name: "topic", type: "string", desc: "Konu filtresi" },
      { name: "subtopic", type: "string", desc: "Alt konu filtresi" },
      { name: "difficulty_level", type: "string", desc: "easy / medium / hard" },
      { name: "has_image", type: "boolean", desc: "Görseli olan sorular (true/false)" },
      { name: "has_animation_video", type: "boolean", desc: "Animasyon videosu olanlar" },
      { name: "has_short_video", type: "boolean", desc: "Kısa video olanlar" },
      { name: "has_horizontal_video", type: "boolean", desc: "Yatay video olanlar" },
      { name: "search", type: "string", desc: "Soru metni, kodu veya ders adında metin araması" }
    ],
    example: `curl "${BASE_URL}/questions?archive_name=Matematik+Soru+Havuzu&limit=10"`,
    response: `{
  "data": [
    {
      "question_code": "MAT-001",
      "archive_name": "Matematik Soru Havuzu",
      "lesson_name": "Matematik",
      "topic": "Denklemler",
      "question_text": "...",
      "option_a": "...", "option_b": "...",
      "option_c": "...", "option_d": "...",
      "correct_answer": "B",
      "solution_text": "...",
      "difficulty_level": "easy",
      "has_image": false,
      "tags": ["denklem"]
    }
  ],
  "meta": { "total": 150, "page": 1, "limit": 10, "pages": 15 }
}`
  },
  {
    method: "GET",
    path: "/questions/random",
    title: "Rastgele Soru Çek",
    description: "Filtre kriterlerine uyan aktif sorular arasından rastgele bir soru döner.",
    params: [
      { name: "archive_name", type: "string", desc: "Arşiv filtresi" },
      { name: "lesson_name", type: "string", desc: "Ders filtresi" },
      { name: "topic", type: "string", desc: "Konu filtresi" },
      { name: "difficulty_level", type: "string", desc: "Zorluk filtresi" },
      { name: "has_image", type: "boolean", desc: "Görsel filtresi" }
    ],
    example: `curl "${BASE_URL}/questions/random?lesson_name=Matematik"`,
    response: `{
  "data": {
    "question_code": "MAT-002",
    "question_text": "...",
    "correct_answer": "C",
    "difficulty_level": "medium"
  }
}`
  },
  {
    method: "GET",
    path: "/questions/{question_code}",
    title: "Soru Kodu ile Soru Çek",
    description: "Belirli bir question_code'a sahip aktif soruyu döner.",
    params: [
      { name: "question_code", type: "path", desc: "Benzersiz soru kodu (örn: MAT-001)" }
    ],
    example: `curl "${BASE_URL}/questions/MAT-001"`,
    response: `{
  "data": {
    "question_code": "MAT-001",
    "archive_name": "Matematik Soru Havuzu",
    "lesson_name": "Matematik",
    "topic": "Denklemler",
    "question_text": "...",
    "option_a": "...", "option_b": "...",
    "option_c": "...", "option_d": "...",
    "correct_answer": "B",
    "solution_text": "...",
    "tags": ["denklem"]
  }
}`
  },
  {
    method: "GET",
    path: "/archives",
    title: "Arşivleri Listele",
    description: "Aktif arşivleri soru sayısıyla birlikte listeler.",
    params: [],
    example: `curl "${BASE_URL}/archives"`,
    response: `{
  "data": [
    {
      "archive_name": "Matematik Soru Havuzu",
      "description": "Temel ve ileri matematik soruları",
      "is_active": true,
      "question_count": 45
    }
  ]
}`
  },
  {
    method: "GET",
    path: "/by-archive/{archive_name}",
    title: "Arşive Göre Sorular",
    description: "Belirli bir arşive ait aktif soruları sayfalı olarak döner.",
    params: [
      { name: "archive_name", type: "path", desc: "Arşiv adı (URL encoded)" },
      { name: "page", type: "integer", default: "1", desc: "Sayfa numarası" },
      { name: "limit", type: "integer", default: "20", desc: "Sayfa başına kayıt" },
      { name: "difficulty_level", type: "string", desc: "Zorluk filtresi" },
      { name: "has_image", type: "boolean", desc: "Görsel filtresi" }
    ],
    example: `curl "${BASE_URL}/by-archive/Matematik%20Soru%20Havuzu?limit=50"`,
    response: `{
  "archive_name": "Matematik Soru Havuzu",
  "data": [...],
  "meta": { "total": 45, "page": 1, "limit": 20, "pages": 3 }
}`
  },
  {
    method: "GET",
    path: "/by-topic/{topic}",
    title: "Konuya Göre Sorular",
    description: "Belirli bir konuya ait aktif soruları döner.",
    params: [
      { name: "topic", type: "path", desc: "Konu adı (URL encoded)" },
      { name: "archive_name", type: "string", desc: "Arşiv filtresi" },
      { name: "difficulty_level", type: "string", desc: "Zorluk filtresi" }
    ],
    example: `curl "${BASE_URL}/by-topic/Denklemler"`,
    response: `{
  "topic": "Denklemler",
  "data": [...],
  "meta": { "total": 12, "page": 1, "limit": 20, "pages": 1 }
}`
  },
  {
    method: "GET",
    path: "/by-lesson/{lesson_name}",
    title: "Derse Göre Sorular",
    description: "Belirli bir derse ait aktif soruları döner.",
    params: [
      { name: "lesson_name", type: "path", desc: "Ders adı (URL encoded)" },
      { name: "archive_name", type: "string", desc: "Arşiv filtresi" },
      { name: "topic", type: "string", desc: "Konu filtresi" },
      { name: "difficulty_level", type: "string", desc: "Zorluk filtresi" }
    ],
    example: `curl "${BASE_URL}/by-lesson/Matematik"`,
    response: `{
  "lesson_name": "Matematik",
  "data": [...],
  "meta": { "total": 30, "page": 1, "limit": 20, "pages": 2 }
}`
  },
  {
    method: "GET",
    path: "/search",
    title: "Soru Ara",
    description: "Soru metni, kod, ders adı ve çözüm metninde tam metin arama.",
    params: [
      { name: "q", type: "string", desc: "Arama terimi (min 2 karakter)" },
      { name: "archive_name", type: "string", desc: "Arşiv filtresi" },
      { name: "lesson_name", type: "string", desc: "Ders filtresi" },
      { name: "page", type: "integer", default: "1", desc: "Sayfa numarası" },
      { name: "limit", type: "integer", default: "20", desc: "Sayfa başına kayıt (max 50)" }
    ],
    example: `curl "${BASE_URL}/search?q=denklem&archive_name=Matematik+Soru+Havuzu"`,
    response: `{
  "query": "denklem",
  "data": [...],
  "meta": { "total": 12, "page": 1, "limit": 20 }
}`
  }
];

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success("Kopyalandı");
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button
      onClick={handleCopy}
      data-testid="copy-code-btn"
      className="p-1.5 text-slate-400 hover:text-slate-200 transition-colors rounded"
    >
      {copied ? <Check size={13} /> : <Copy size={13} />}
    </button>
  );
}

function EndpointCard({ ep }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid={`endpoint-${ep.method.toLowerCase()}-${ep.path.replace(/[^a-z0-9]/gi, "-")}`}>
      <button
        onClick={() => setExpanded((e) => !e)}
        className="w-full flex items-center gap-3 px-5 py-4 hover:bg-slate-50 transition-colors text-left"
      >
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-blue-50 text-blue-600 border border-blue-100 shrink-0">
          {ep.method}
        </span>
        <code className="text-sm font-mono text-slate-700">{ep.path}</code>
        <span className="text-xs text-slate-400 ml-2 hidden sm:block">{ep.title}</span>
        <div className="ml-auto">
          {expanded ? <ChevronUp size={14} className="text-slate-400" /> : <ChevronDown size={14} className="text-slate-400" />}
        </div>
      </button>

      {expanded && (
        <div className="border-t border-slate-100 px-5 py-4 space-y-4">
          <p className="text-sm text-slate-600">{ep.description}</p>

          {ep.params.length > 0 && (
            <div>
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">Parametreler</p>
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="text-left py-1.5 text-slate-500 font-medium w-32">Ad</th>
                    <th className="text-left py-1.5 text-slate-500 font-medium w-20">Tür</th>
                    <th className="text-left py-1.5 text-slate-500 font-medium">Açıklama</th>
                  </tr>
                </thead>
                <tbody>
                  {ep.params.map((p) => (
                    <tr key={p.name}>
                      <td className="py-1.5 font-mono text-blue-600">{p.name}</td>
                      <td className="py-1.5 text-slate-400">{p.type}</td>
                      <td className="py-1.5 text-slate-600">
                        {p.desc}{p.default ? <span className="text-slate-400"> (varsayılan: {p.default})</span> : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div>
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">Örnek İstek</p>
            <div className="bg-slate-900 rounded-md p-3 flex items-start justify-between gap-2">
              <code className="text-xs font-mono text-slate-300 break-all">{ep.example}</code>
              <CopyButton text={ep.example} />
            </div>
          </div>

          <div>
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">Örnek Yanıt</p>
            <div className="bg-slate-900 rounded-md p-3 flex items-start justify-between gap-2">
              <pre className="text-xs font-mono text-slate-300 overflow-x-auto">{ep.response}</pre>
              <CopyButton text={ep.response} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function ApiAccess() {
  const [copiedBase, setCopiedBase] = useState(false);

  const copyBase = () => {
    navigator.clipboard.writeText(BASE_URL);
    setCopiedBase(true);
    toast.success("Base URL kopyalandı");
    setTimeout(() => setCopiedBase(false), 2000);
  };

  return (
    <div className="p-8 fade-in">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900">
          Public API
        </h1>
        <p className="text-sm text-slate-500 mt-0.5">
          Diğer uygulamalarınızın soru bankasına erişmesi için kullanabileceği API endpointleri
        </p>
      </div>

      {/* Base URL */}
      <div className="bg-white border border-slate-200 rounded-lg p-5 mb-6">
        <div className="flex items-center gap-2 mb-2">
          <Globe size={14} className="text-blue-500" />
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Base URL</p>
        </div>
        <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-md p-3">
          <code className="text-sm font-mono text-slate-700 flex-1 break-all" data-testid="base-url-display">{BASE_URL}</code>
          <button
            onClick={copyBase}
            data-testid="copy-base-url-btn"
            className="p-1.5 text-slate-400 hover:text-slate-600 transition-colors rounded shrink-0"
          >
            {copiedBase ? <Check size={14} /> : <Copy size={14} />}
          </button>
        </div>
        <div className="flex items-center gap-2 mt-3">
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200">
            Auth Gerekmiyor
          </span>
          <span className="text-xs text-slate-400">Sadece aktif sorular döner</span>
        </div>
      </div>

      {/* Endpoints */}
      <div className="space-y-3">
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Endpointler</p>
          <a
            href={`${BACKEND_URL}/docs`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="swagger-link"
            className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700"
          >
            Swagger Docs <ExternalLink size={11} />
          </a>
        </div>
        {ENDPOINTS.map((ep) => (
          <EndpointCard key={ep.path} ep={ep} />
        ))}
      </div>

      {/* Code example */}
      <div className="bg-white border border-slate-200 rounded-lg p-5 mt-6">
        <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-3">JavaScript Örneği</p>
        <div className="bg-slate-900 rounded-md p-4">
          <pre className="text-xs font-mono text-slate-300 overflow-x-auto">{`// question_code ile soru çekme
const response = await fetch('${BASE_URL}/questions/MAT-001');
const { data } = await response.json();
console.log(data.question_text);

// Arşiv filtreli liste
const list = await fetch(
  '${BASE_URL}/questions?lesson_name=Matematik&limit=50'
);
const { data: questions, meta } = await list.json();
console.log(\`\${meta.total} soru bulundu\`);`}</pre>
        </div>
      </div>
    </div>
  );
}
